import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Compass, Clock, Users, MapPin, Loader2, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { breadcrumbLD, eventsItemListLD } from '../utils/structuredData';
import { cityName } from '../utils/seoFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /ex — global experiences directory
 * /ex/:city — city-scoped experiences directory
 *
 * Mirrors the `/events/{city}` directory shape so SEO crawlers see a
 * consistent ItemList + BreadcrumbList graph across all three product
 * kinds (events / experiences / reservations).
 */
export default function ExperienceDirectory() {
  const { city: cityParam } = useParams();
  const cityNice = cityParam ? cityName(cityParam) : null;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [maxDuration, setMaxDuration] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (cityParam) params.set('city', cityParam);
    if (q.trim()) params.set('q', q.trim());
    if (maxDuration) params.set('max_duration', maxDuration);
    params.set('limit', '60');
    axios.get(`${API}/experiences?${params}`)
      .then(res => setRows(res.data?.experiences || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [cityParam, q, maxDuration]);

  const title = cityNice ? `Experiences in ${cityNice}` : 'Bookable Experiences';
  const description = cityNice
    ? `Tours, tastings, and bookable experiences in ${cityNice}. Reserve your spot instantly on Eventism.`
    : 'Browse tours, tastings, and bookable experiences across every city. Reserve your spot in seconds on Eventism.';

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://eventism.com';
  const itemList = useMemo(
    () => eventsItemListLD(
      rows.map(r => ({ ...r, _publicUrlPrefix: '/ex' })),
      title,
      baseUrl,
    ),
    [rows, title, baseUrl]
  );
  const crumbs = breadcrumbLD(
    cityNice
      ? [
          { name: 'Home', url: '/' },
          { name: 'Experiences', url: '/ex' },
          { name: cityNice, url: `/ex/${cityParam}` },
        ]
      : [
          { name: 'Home', url: '/' },
          { name: 'Experiences', url: '/ex' },
        ]
  );

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="experience-directory">
      <SEO
        title={`${title} · Eventism`}
        description={description}
        keywords={[
          'experiences', 'tours', 'tastings', 'classes',
          cityNice ? `experiences in ${cityNice}` : 'experiences near me',
          cityNice ? `${cityNice} tours` : 'tours near me',
        ].filter(Boolean)}
        structuredData={[itemList, crumbs]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero — mirrors the /events/{city} hero treatment */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <Compass className="h-3.5 w-3.5" />
            <span className="uppercase tracking-wider">Bookable experiences</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3" data-testid="experience-directory-title">
            {cityNice ? <>Experiences in <span className="text-emerald-500">{cityNice}</span></> : <>Find your next <span className="text-emerald-500">experience</span></>}
          </h1>
          <p className="text-base text-muted-foreground max-w-2xl">{description}</p>
        </div>

        {/* Filter strip */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search experiences…"
              className="pl-9"
              data-testid="exp-dir-search"
            />
          </div>
          <select
            value={maxDuration}
            onChange={e => setMaxDuration(e.target.value)}
            className="h-10 px-3 border rounded-md bg-background text-sm"
            data-testid="exp-dir-duration"
          >
            <option value="">Any duration</option>
            <option value="60">Under 1 hour</option>
            <option value="120">Under 2 hours</option>
            <option value="180">Under 3 hours</option>
            <option value="240">Under 4 hours</option>
          </select>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="exp-dir-empty">
            <Compass className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No experiences found{cityNice ? ` in ${cityNice}` : ''}. Check back soon — hosts are adding new tours every week.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="exp-dir-grid">
            {rows.map(r => <ExperienceCard key={r.event_id} item={r} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function ExperienceCard({ item }) {
  const minPrice = item.min_price_cents ? (item.min_price_cents / 100) : null;
  return (
    <Link
      to={`/ex/${item.city}/${item.slug}`}
      className="group rounded-xl overflow-hidden border border-border bg-card hover:border-emerald-500/60 hover:shadow-lg transition-all"
      data-testid={`exp-card-${item.event_id}`}
    >
      <div className="aspect-video bg-muted overflow-hidden relative">
        {item.image_url ? (
          <img src={item.image_url} alt={item.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center text-muted-foreground"><Compass className="h-10 w-10 opacity-40" /></div>
        )}
        {item.open_slot_count > 0 && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-emerald-500/90 text-white text-[10px] font-medium">
            {item.open_slot_count} slot{item.open_slot_count === 1 ? '' : 's'} open
          </span>
        )}
      </div>
      <div className="p-4 space-y-2">
        <h3 className="font-semibold leading-tight group-hover:text-emerald-500 transition">{item.name}</h3>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
          {item.duration_minutes && (
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {item.duration_minutes} min</span>
          )}
          {item.max_group_size && (
            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> ≤{item.max_group_size}</span>
          )}
          {item.city && (
            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {cityName(item.city)}</span>
          )}
        </div>
        <div className="flex items-center justify-between pt-1">
          <p className="text-xs text-muted-foreground line-clamp-2 flex-1">
            {item.short_description || item.tagline}
          </p>
          {minPrice ? (
            <p className="text-sm font-semibold text-emerald-500 whitespace-nowrap ml-2">
              from ${minPrice.toFixed(0)}
            </p>
          ) : null}
        </div>
      </div>
    </Link>
  );
}
